import { cn } from '../../lib/cn';
import { righePercorso, type RigaPercorso } from '../../lib/eventi/programma';
import type { Percorso, ProgrammaEvento } from '@eventi/tipi.ts';

type Props = {
  programma: ProgrammaEvento;
  /** Quello che la persona ha chiesto (se c'e'). */
  richiesta?: Percorso | null;
  /** Quello che le e' stato assegnato (se c'e'). */
  assegnazione?: Percorso | null;
  /** Mostra la colonna dell'assegnazione anche se vuota. */
  conAssegnazione?: boolean;
};

function Cella({ riga }: { riga: RigaPercorso }) {
  if (riga.mancante) return <span className="text-red-700">Incontro non piu' in programma ({riga.codice})</span>;
  if (!riga.codice) return <span className="text-neutral-400">—</span>;
  return (
    <span>
      <span className="font-medium text-gray-900">{riga.aula}</span>
      <span className="text-neutral-600"> · {riga.titolo}</span>
    </span>
  );
}

/**
 * Il percorso di una persona turno per turno: cosa ha chiesto e, accanto,
 * cosa le e' stato assegnato. Le righe dove i due non coincidono sono
 * evidenziate.
 */
export function TabellaPercorso({ programma, richiesta, assegnazione, conAssegnazione }: Props) {
  const chiesto = righePercorso(programma, richiesta);
  const dato = righePercorso(programma, assegnazione);
  const mostraRichiesta = richiesta !== undefined;
  const mostraAssegnazione = conAssegnazione || Boolean(assegnazione);

  if (chiesto.length === 0) {
    return <p className="text-xs text-neutral-400">Il programma dell'evento non e' ancora stato importato.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500">
            <th className="py-1.5 pr-3 font-medium">Turno</th>
            {mostraRichiesta && <th className="py-1.5 pr-3 font-medium">Ha chiesto</th>}
            {mostraAssegnazione && <th className="py-1.5 font-medium">Assegnato</th>}
          </tr>
        </thead>
        <tbody>
          {chiesto.map((riga, i) => {
            const diverso = mostraRichiesta && mostraAssegnazione && riga.codice !== dato[i].codice;
            const nuovoGiorno = i === 0 || chiesto[i - 1].giorno !== riga.giorno;
            return (
              <tr key={`${riga.giorno}-${riga.turno}`} className={cn('border-t border-gray-100', diverso && 'bg-amber-50')}>
                <td className="py-1.5 pr-3 align-top whitespace-nowrap text-neutral-600">
                  {nuovoGiorno && <span className="block font-semibold text-gray-900">{riga.giornoNome}</span>}
                  Turno {riga.turno} <span className="text-neutral-400">{riga.orario}</span>
                </td>
                {mostraRichiesta && (
                  <td className="py-1.5 pr-3 align-top">
                    <Cella riga={riga} />
                  </td>
                )}
                {mostraAssegnazione && (
                  <td className="py-1.5 align-top">
                    <Cella riga={dato[i]} />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
